export const getEmailUsername = (email) => {
  if (!email) {
    return '';
  }
  return email.split('@')[0];
};

export const isAdmin = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('admin')) {
    return true;
  }
  return false;
};

export const isFinanceAdmin = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('finance-admin')) {
    return true;
  }
  return false;
};

export const isAnyAdmin = (user) => {
  return isAdmin(user) || isFinanceAdmin(user);
};

export const isViewOnly = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('view-only')) {
    return true;
  }
  return false;
};

export const isStandardUser = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('standard-user')) {
    return true;
  }
  return false;
};

export const isSuperUser = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('super-user')) {
    return true;
  }
  return false;
};

export const isSuperUserPii = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('super-user-pii')) {
    return true;
  }
  return false;
};

// `activations-user` is the role that gates the Activations module. It is
// granted independently of the audience-purchase tiers (so a super-user
// without this role still cannot see Activations). Admins always have access.
export const isActivationsUser = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('activations-user')) {
    return true;
  }
  return false;
};

export const canUseActivations = (user) => {
  return isActivationsUser(user) || isAdmin(user);
};

// ATAD-146: `model-files-user` gates importing from the Alliant data sources
// (the Upload File modal's CMDA, First Party and Syndicated Alliant sources
// and their shared-folder lists). Shown as "Alliant Data Sources". Like
// activations-user it's an add-on granted independently of the user tier;
// admins always pass.
export const isModelFilesUser = (user) => {
  if (user && user.roles && user.roles.length && user.roles.includes('model-files-user')) {
    return true;
  }
  return false;
};

// The server also requires upload permission for listing and importing
// (standardUserProtected, as for every upload), so the add-on alone — say on
// a view-only user — isn't enough. Using an already-imported model in the
// builder needs neither: only ownership.
export const canUseModelFiles = (user) => {
  return (isModelFilesUser(user) || isAdmin(user)) && canUploadFiles(user);
};

// Capability-based checking functions
export const canUploadFiles = (user) => {
  return isStandardUser(user) || isSuperUser(user) || isSuperUserPii(user) || isAdmin(user);
};

export const canOrderAudience = (user) => {
  return isStandardUser(user) || isSuperUser(user) || isSuperUserPii(user) || isAdmin(user);
};

export const canOrderProfile = (user) => {
  return isStandardUser(user) || isSuperUser(user) || isSuperUserPii(user) || isAdmin(user);
};

// Profile reports for AUDIENCES (orderType audience-profile) are gated
// behind super-user on the server (openOrder / completeOrder), unlike
// seed-file profiles which any standard user can order.
export const canOrderAudienceProfile = (user) => {
  return isSuperUser(user) || isSuperUserPii(user) || isAdmin(user);
};

export const canOrderEnhancement = (user) => {
  return isSuperUser(user) || isSuperUserPii(user) || isAdmin(user);
};

export const canDistributeFiles = (user) => {
  return isSuperUser(user) || isSuperUserPii(user) || isAdmin(user);
};

export const canDownloadPii = (user) => {
  return isSuperUserPii(user) || isAdmin(user);
};

export const capitalize = (str) => {
  if (typeof str === 'string' && str.length > 0) {
    return str.substring(0, 1).toUpperCase() + str.substring(1, str.length);
  }
  return str;
};

export const getUserInitial = (user) => {
  if (!user) {
    return '...';
  }

  const { email, name } = user;
  const firstLetter = (str) => str.substring(0, 1).toUpperCase();

  if (name) {
    return firstLetter(name);
  }
  if (email) {
    return firstLetter(email);
  }
};

export const incrementName = (name) => {
  const match = /\s\((\d+)\)$/.exec(name.trim());

  if (!match) {
    return `${name} (1)`;
  }

  return `${name.substring(0, match.index)} (${++match[1]})`;
};

export const incrementNameNoConflicts = (name, names) => {
  let incrementedName = incrementName(name);
  let nameConflict = names.includes(incrementedName);

  while (nameConflict) {
    incrementedName = incrementName(incrementedName);
    nameConflict = names.includes(incrementedName);
  }

  return incrementedName;
};

export const stripFileType = (str, fileType) => {
  if (typeof str !== 'string') {
    return str;
  }
  return str.split(fileType)[0];
};

export const currency = (input, minDecimalDigits = 2, maxDecimaDigits = 2) => {
  let number = typeof input === 'number' ? input : parseFloat(input);

  if (isNaN(number)) {
    return '$ --';
  }

  const formattedNumber = number.toLocaleString(undefined, {
    minimumFractionDigits: minDecimalDigits,
    maximumFractionDigits: maxDecimaDigits,
  });
  return `$${formattedNumber}`;
};

export const dateIsInPast = (dateString) => {
  const date = new Date(Number(dateString));
  const now = Date.now();
  return date.getTime() < now;
};

export const getUserDisplayRole = (user) => {
  if (!user || !user.roles || !user.roles.length) {
    return null;
  }

  const roleDisplayMap = {
    'admin': 'Admin',
    'finance-admin': 'Finance Admin',
    'super-user-pii': 'Super User (PII)',
    'super-user': 'Super User',
    'standard-user': 'Standard User',
    'view-only': 'View Only',
    'activations-user': 'Activations User',
    'model-files-user': 'Alliant Data Sources'
  };

  // `activations-user` and `model-files-user` (ATAD-146) are capability
  // add-ons rather than primary tiers, so they sit below all of the user
  // tiers but above nothing.
  const rolePriority = ['admin', 'finance-admin', 'super-user-pii', 'super-user', 'standard-user', 'view-only', 'activations-user', 'model-files-user'];
  
  for (const role of rolePriority) {
    if (user.roles.includes(role)) {
      return roleDisplayMap[role];
    }
  }
  
  return null;
};
